// Declarative model config and input normalizers for every path that writes a
// scenario record: the session-free maintenance cores (SAL-2499), the admin
// API routes behind the scenario editor, and the create tools. Kept separate
// from the core logic so each file has one clear responsibility. No database
// or session access lives here. Each SCENARIO_CONFIG entry describes one
// scenario model: the fields an author may write and how each is read, the
// unique-name field, the relations it carries, the optional soft-archive flag,
// and which relations block a hard delete (the destructive-delete guard).
import { normalizePersonaDirective } from "@/lib/scenarios/persona-directive";
import {
  normalizeContactType,
  normalizeCustomerStatus,
  normalizeMaxTurns,
  normalizePlayChannel,
} from "@/lib/scenario-tools/scenario-settings";

// A bound on agent input, not a content rule: a pasted call script or brief
// runs past 5,000 characters, and the editor saves through the same
// normalizers as the MCP tools.
const MAX_TEXT_LENGTH = 20000;

export function normalizeWorkspaceId(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed.length > 120 ? trimmed.slice(0, 120) : trimmed;
}

/**
 * A whole, safe integer from a number or a digits-only string, else null.
 * `parseInt` would read "12junk" as 12 and "1e3" as 1, so a malformed id from a
 * route or tool argument could quietly land on a real record; this reads the
 * complete input or nothing.
 */
export function parseIntegerId(value) {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) ? value : null;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!/^[+-]?\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * A request body's maxTurns: null for blank, null or undefined (Unlimited),
 * the whole number it names, or undefined when it names neither -- the
 * caller answers that with 400. The seven scenario create routes each read
 * this with parseInt, which took "12junk" as 12.
 */
export function readMaxTurns(value) {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseIntegerId(value);
  return parsed === null ? undefined : parsed;
}

function trimText(value, maxLength = MAX_TEXT_LENGTH) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
}

function normalizeStringArrayValue(value, maxItems = 50, maxLength = 2000) {
  if (!Array.isArray(value)) return undefined;
  return value
    .map((entry) => trimText(entry, maxLength))
    .filter(Boolean)
    .slice(0, maxItems);
}

// Build a Prisma `data` payload from an updates object, applying per-field
// normalizers. Reject unsupported keys and invalid values before any write;
// undefined means omitted. Empty payloads are caught by the caller.
export function buildUpdateData(updates, fieldConfig) {
  const data = {};
  if (!updates || typeof updates !== "object" || Array.isArray(updates)) {
    throw new TypeError("Updates must be an object.");
  }
  const unsupported = Object.keys(updates).filter((field) => !Object.hasOwn(fieldConfig, field));
  if (unsupported.length) {
    throw new TypeError(`Unsupported update fields: ${unsupported.join(", ")}.`);
  }

  for (const [field, config] of Object.entries(fieldConfig)) {
    if (!Object.hasOwn(updates, field)) continue;
    const rawValue = updates[field];
    if (rawValue === undefined) continue;

    const normalized = config.normalize(rawValue);
    if (normalized === undefined) throw new TypeError(`Invalid value for ${field}.`);
    data[config.prismaField || field] = normalized;
  }

  return data;
}

// Nullable free text: empty string clears the column to null.
const TEXT = {
  normalize: (value) => {
    if (value === null) return null;
    if (typeof value !== "string") return undefined;
    const trimmed = trimText(value);
    return trimmed || null;
  },
};

// Required text: blank or non-string is invalid (column is never cleared).
const REQUIRED_TEXT = {
  normalize: (value) => {
    if (typeof value !== "string") return undefined;
    const trimmed = trimText(value);
    return trimmed || undefined;
  },
};

const STRING_ARRAY = {
  normalize: (value) => normalizeStringArrayValue(value),
};

const BOOLEAN = {
  normalize: (value) => (typeof value === "boolean" ? value : undefined),
};

const MAX_TURNS = { normalize: normalizeMaxTurns };
const CHANNEL = { normalize: normalizePlayChannel };
const CUSTOMER_STATUS = { normalize: normalizeCustomerStatus };
const CONTACT_TYPE = { normalize: normalizeContactType };

// Scenario-level private buyer instructions (SAL-3128). Optional on every type.
// A blank value clears the column; anything that is not text throws, and the
// edit core turns that into the caller's error message.
const PERSONA_DIRECTIVE = {
  prismaField: "personaDirective",
  normalize: (value) => normalizePersonaDirective(value),
};

const INTEGER = {
  normalize: (value) => {
    if (value === null) return null;
    const parsed = parseIntegerId(value);
    return parsed === null ? undefined : parsed;
  },
};

const STAGE = {
  normalize: (value) =>
    String(value || "").toUpperCase() === "LATE" ? "LATE" : "EARLY",
};

// Per entry:
//   model         the Prisma delegate
//   label         the noun in messages ("objection", "pitch scenario")
//   uniqueField   the column a scenario's name lives in, unique per studio
//   editableFields  every column an author may write, with its normalizer
//   glossaryGroup   the model links a glossary group (all but management)
//   attachments     the model owns uploaded attachments (plays)
//   authored        the model records which member created it (plays)
//   listOrderBy     ordering the list route applies before the name
//   archiveField    the soft-archive flag, where the model has one
//   dependents      relations that block a hard delete
export const SCENARIO_CONFIG = {
  objection: {
    model: "customerObjection",
    label: "objection",
    uniqueField: "objection",
    glossaryGroup: true,
    // High-priority objections list first; priority 999 is the default.
    listOrderBy: [{ priority: "asc" }],
    editableFields: {
      objection: REQUIRED_TEXT,
      response: STRING_ARRAY,
      realConcern: TEXT,
      keyMessage: TEXT,
      opening: TEXT,
      customerOpening: TEXT,
      situationBackground: TEXT,
      onboardingAccommodation: TEXT,
      personaDirective: PERSONA_DIRECTIVE,
      goal: TEXT,
      stage: STAGE,
      priority: INTEGER,
      url: TEXT,
      additionalUrl: TEXT,
      passiveMode: BOOLEAN,
      questionMode: BOOLEAN,
      requiredQuestionsBot: STRING_ARRAY,
      rubricId: INTEGER,
      maxTurns: MAX_TURNS,
    },
    dependents: [
      {
        label: "practice results",
        model: "objectionSimulationResult",
        fk: "customerObjectionId",
      },
      { label: "assignments", model: "rolePlay", fk: "customerObjectionId" },
    ],
  },
  pitch: {
    model: "pitch",
    label: "pitch scenario",
    uniqueField: "pitch",
    glossaryGroup: true,
    editableFields: {
      pitch: REQUIRED_TEXT,
      overview: REQUIRED_TEXT,
      content: TEXT,
      customerOpening: TEXT,
      focus: TEXT,
      goal: TEXT,
      type: TEXT,
      url: TEXT,
      additionalUrl: TEXT,
      situationBackground: TEXT,
      onboardingAccommodation: TEXT,
      personaDirective: PERSONA_DIRECTIVE,
      examples: STRING_ARRAY,
      singleQuestion: BOOLEAN,
      passiveMode: BOOLEAN,
      questionMode: BOOLEAN,
      requiredQuestionsBot: STRING_ARRAY,
      maxTurns: MAX_TURNS,
    },
    dependents: [
      { label: "practice results", model: "pitchSimulationResult", fk: "pitchId" },
      { label: "assignments", model: "rolePlay", fk: "pitchId" },
    ],
  },
  discovery: {
    model: "discovery",
    label: "discovery scenario",
    uniqueField: "trackName",
    glossaryGroup: true,
    editableFields: {
      trackName: REQUIRED_TEXT,
      questions: STRING_ARRAY,
      requiredQuestions: STRING_ARRAY,
      opening: TEXT,
      customerOpening: TEXT,
      goal: TEXT,
      url: TEXT,
      additionalUrl: TEXT,
      situationBackground: TEXT,
      onboardingAccommodation: TEXT,
      personaDirective: PERSONA_DIRECTIVE,
      passiveMode: BOOLEAN,
      questionMode: BOOLEAN,
      requiredQuestionsBot: STRING_ARRAY,
      maxTurns: MAX_TURNS,
    },
    dependents: [
      {
        label: "practice results",
        model: "discoverySimulationResult",
        fk: "discoveryId",
      },
      { label: "assignments", model: "rolePlay", fk: "discoveryId" },
    ],
  },
  outbound: {
    model: "coldCall",
    label: "outbound scenario",
    uniqueField: "name",
    glossaryGroup: true,
    editableFields: {
      name: REQUIRED_TEXT,
      script: TEXT,
      customerOpening: TEXT,
      url: TEXT,
      situationBackground: TEXT,
      onboardingAccommodation: TEXT,
      personaDirective: PERSONA_DIRECTIVE,
      examples: STRING_ARRAY,
      passiveMode: BOOLEAN,
      questionMode: BOOLEAN,
      requiredQuestionsBot: STRING_ARRAY,
      maxTurns: MAX_TURNS,
    },
    dependents: [
      // Cold-call practice is stored in the shared SimulationResult table
      // (simulationId = ColdCall id, simulationType = COLD_CALL), not a
      // dedicated *SimulationResult model, so it needs the extra where filter.
      {
        label: "practice results",
        model: "simulationResult",
        fk: "simulationId",
        where: { simulationType: "COLD_CALL" },
      },
      { label: "assignments", model: "rolePlay", fk: "coldCallId" },
    ],
  },
  play: {
    model: "play",
    label: "play scenario",
    uniqueField: "name",
    glossaryGroup: true,
    attachments: true,
    authored: true,
    editableFields: {
      name: REQUIRED_TEXT,
      channel: CHANNEL,
      customerStatus: CUSTOMER_STATUS,
      contactType: CONTACT_TYPE,
      maxTurns: MAX_TURNS,
      personaDirective: PERSONA_DIRECTIVE,
      situationBackground: TEXT,
      customerOpening: TEXT,
      happenedAlready: TEXT,
      previousContactChanges: TEXT,
      anythingNew: TEXT,
      assumptions: STRING_ARRAY,
      criticalInformation: STRING_ARRAY,
      personasMainConcerns: STRING_ARRAY,
      goal: TEXT,
      otherInformation: TEXT,
      url: TEXT,
      additionalUrl: TEXT,
      onboardingAccommodation: TEXT,
      passiveMode: BOOLEAN,
      questionMode: BOOLEAN,
      requiredQuestionsBot: STRING_ARRAY,
    },
    dependents: [
      { label: "practice results", model: "playSimulationResult", fk: "playId" },
      { label: "assignments", model: "rolePlay", fk: "playId" },
    ],
  },
  management: {
    model: "managementScenario",
    label: "management scenario",
    uniqueField: "name",
    archiveField: "isActive",
    editableFields: {
      name: REQUIRED_TEXT,
      description: TEXT,
      situationBackground: TEXT,
      sellerOpening: TEXT,
      context: TEXT,
      objectives: STRING_ARRAY,
      commonChallenges: STRING_ARRAY,
      personaDirective: PERSONA_DIRECTIVE,
      maxTurns: MAX_TURNS,
      isActive: BOOLEAN,
    },
    dependents: [
      {
        label: "practice results",
        model: "managementSimulationResult",
        fk: "managementScenarioId",
      },
      { label: "assignments", model: "rolePlay", fk: "managementScenarioId" },
    ],
  },
};

/** The config entry whose Prisma delegate is `model`, or null. */
export function scenarioConfigForModel(model) {
  return Object.values(SCENARIO_CONFIG).find((entry) => entry.model === model) ?? null;
}

/** The one wording for a name already taken in the studio, on every write path. */
export function nameTakenMessage(config) {
  return `Another ${config.label} with this name already exists in this studio.`;
}
